import { booleanAttribute, Component, ElementRef, EventEmitter, HostBinding, Input, Output, ViewChild } from "@angular/core";

type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'outline'
    | 'ghost'
    | 'danger'
    | 'danger-soft'
    | 'solid'
    | 'soft';
type ButtonColor = 'blue' | 'green' | 'red' | 'neutral' | 'white';
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonAppearance = 'attachment-action' | 'toolbar-primary';
type ButtonIconPresetSize = 'sm' | 'md' | 'base' | 'lg';
type ButtonIconSize = ButtonIconPresetSize | number | `${number}`;
type ButtonIconDirection = 'none' | 'up' | 'down' | 'left' | 'right';
type ButtonLoadingVariant = 'spinner' | 'dots' | 'ring';

const BUTTON_ICON_PRESET_SIZES: readonly string[] = ['sm', 'md', 'base', 'lg'];

@Component({
    selector: "core-ui-button-base",
    templateUrl: "./button-base.component.html",
    styleUrls: ["./button-base.component.scss"],
    standalone: false,
})
export class ButtonBaseComponent {
    @ViewChild('button', { static: true }) private button?: ElementRef<HTMLButtonElement>;

    @Input() type: 'button' | 'submit' | 'reset' = 'button';
    @Input() variant: ButtonVariant = 'solid';
    @Input() color: ButtonColor = 'neutral';
    @Input() size?: ButtonSize;
    @Input() block = false;
    @Input() stretch = false;
    @Input() disabled = false;
    @Input() active = false;
    @Input({ transform: booleanAttribute }) iconOnly = false;
    @Input({ transform: booleanAttribute }) loading = false;
    @Input() loadingVariant: ButtonLoadingVariant = 'dots';
    @Input() appearance?: ButtonAppearance;
    @Input() btnClass: string | string[] = '';
    @Input() height?: string;
    @Input() label = '';
    @Input() icon = '';
    @Input() iconColor?: string;
    @Input() iconSize: ButtonIconSize = 'md';
    @Input() iconDirection: ButtonIconDirection = 'none';
    @Input() iconClass = '';
    @Input() ariaLabel = '';
    @Input() ariaHasPopup?: 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
    @Input() ariaExpanded?: boolean;
    @Input() ariaControls?: string;
    @Input() ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';

    @Output() clicked = new EventEmitter<MouseEvent>();

    @HostBinding('class.is-block')
    get hostBlock(): boolean {
        return this.block;
    }

    get variantClass(): string {
        return `klab-btn--${this.variant}`;
    }

    get colorClass(): string {
        return `klab-btn--${this.color}`;
    }

    get appearanceClass(): string {
        return this.appearance ? `klab-btn--${this.appearance}` : '';
    }

    get hasMouseAnimation(): boolean {
        return this.appearance === 'toolbar-primary';
    }

    get iconPresetSize(): ButtonIconPresetSize {
        return typeof this.iconSize === 'string' && BUTTON_ICON_PRESET_SIZES.includes(this.iconSize)
            ? this.iconSize as ButtonIconPresetSize
            : 'lg';
    }

    get customIconSizePx(): number | null {
        if (typeof this.iconSize === 'string' && BUTTON_ICON_PRESET_SIZES.includes(this.iconSize)) {
            return null;
        }

        const size = Number(this.iconSize);
        return Number.isFinite(size) && size > 0 ? size : null;
    }

    onClick(event: MouseEvent) {
        if (this.disabled || this.loading) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        this.clicked.emit(event);
    }

    focus(): void {
        this.button?.nativeElement.focus();
    }

}
